import React, { useEffect, useState } from "react";
import {
  Space,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Typography,
  Divider,
} from "antd";
import { SettingOutlined, FolderOpenOutlined } from "@ant-design/icons";
import { useDesigner } from "@designable/react";
import { observer } from "@formily/react";
import {
  transformToSchema,
  transformToTreeNode,
} from "@designable/formily-transformer";
import {
  getTemplateTypes,
  createForm,
  listForms,
  listVersions,
  getVersion,
  createDraft,
} from "../api";

const { Option } = Select;
const { Text } = Typography;

// Form types / phases mirror the backend enums (main.models).
const FORM_TYPES = ["Dynamic", "RiskAssessment", "LinkedObject", "Comment"];
const PHASES = ["Draft", "InProgress", "Live", "Closeout"];

const EMPTY_META = {
  templateTypeId: undefined,
  templateId: undefined,
  title: "",
  type: "Dynamic",
  order: 1,
  phase: "Draft",
  note: "",
};

export const ActionsWidget = observer(({ onSignOut }) => {
  const designer = useDesigner();

  const [templateTypes, setTemplateTypes] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [formId, setFormId] = useState(null); // null = new form
  const [saving, setSaving] = useState(false);

  const [metaOpen, setMetaOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // null | 'draft' | 'publish'
  const [openOpen, setOpenOpen] = useState(false);
  const [formsList, setFormsList] = useState([]);
  const [pickTemplateId, setPickTemplateId] = useState(undefined);

  // A minimal empty form tree used to reset the canvas for a new form.
  const EMPTY_TREE = { form: {}, schema: { type: "object" } };

  // Load the template-type/template tree once authenticated.
  const loadTemplateTypes = async () => {
    try {
      setTemplateTypes(await getTemplateTypes());
    } catch (e) {
      message.error("Failed to load templates: " + e.message);
    }
  };

  useEffect(() => {
    loadTemplateTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const templatesForType = (typeId) => {
    const tt = templateTypes.find((t) => t.id === typeId);
    return (tt && tt.templates) || [];
  };

  const currentSchema = () => transformToSchema(designer.getCurrentTree());

  // --- save / publish ------------------------------------------------------ //
  // Required metadata to persist a form.
  const metaValid = () => !!meta.templateId && !!meta.title.trim();

  // Entry point for the Save draft / Publish buttons. If the required settings
  // are already filled, persist straight away; otherwise open the settings modal
  // once and remember the intent, so the very next "Done" completes the action.
  const requestSave = (publish) => {
    if (metaValid()) {
      doSave(publish);
    } else {
      setPendingAction(publish ? "publish" : "draft");
      setMetaOpen(true);
    }
  };

  const doSave = async (publish) => {
    setSaving(true);
    try {
      const schema_json = currentSchema();
      if (formId) {
        // Existing form: update the working draft (optionally publish it).
        const v = await createDraft(formId, {
          schema_json,
          note: meta.note,
          publish,
        });
        message.success(
          `Saved v${v.version} (${publish ? "published" : "draft"})`
        );
      } else {
        // New form: create it + its first version.
        const form = await createForm({
          template: meta.templateId,
          title: meta.title,
          type: meta.type,
          order: meta.order,
          phase: meta.phase,
          schema_json,
          publish,
          note: meta.note,
        });
        setFormId(form.id);
        message.success(
          `Created "${form.title}" ${publish ? "(published)" : "(draft)"}`
        );
      }
    } catch (e) {
      message.error("Save failed: " + e.message);
    }
    setSaving(false);
  };

  // "Done" in the settings modal. When a Save/Publish is pending (the modal was
  // opened because settings were missing), complete it; otherwise just close.
  const handleMetaDone = async () => {
    if (!pendingAction) {
      setMetaOpen(false);
      return;
    }
    if (!metaValid()) {
      message.warning("Choose a Template and enter a Form title.");
      return;
    }
    const publish = pendingAction === "publish";
    setPendingAction(null);
    setMetaOpen(false);
    await doSave(publish);
  };

  const handleMetaCancel = () => {
    setMetaOpen(false);
    setPendingAction(null); // aborting the modal cancels any pending save
  };

  // --- open existing ------------------------------------------------------- //
  const openPicker = async () => {
    setOpenOpen(true);
    setFormsList([]);
    setPickTemplateId(undefined);
  };

  const loadFormsForTemplate = async (templateId) => {
    setPickTemplateId(templateId);
    try {
      setFormsList(await listForms(templateId));
    } catch (e) {
      message.error("Failed to list forms: " + e.message);
    }
  };

  const loadFormIntoDesigner = async (form) => {
    try {
      const versions = await listVersions(form.id);
      // Prefer the published version; otherwise the latest.
      const target =
        versions.find((v) => v.status === "Published") || versions[0];
      const full = await getVersion(form.id, target.version);
      designer.setCurrentTree(transformToTreeNode(full.schema_json));
      setFormId(form.id);
      setMeta({
        templateTypeId: templateTypes.find((tt) =>
          (tt.templates || []).some((t) => t.id === form.template)
        )?.id,
        templateId: form.template,
        title: form.title,
        type: form.type,
        order: form.order,
        phase: form.phase,
        note: "",
      });
      setOpenOpen(false);
      message.success(`Opened "${form.title}" (editing v${target.version})`);
    } catch (e) {
      message.error("Failed to open form: " + e.message);
    }
  };

  const startNew = () => {
    // Open a fresh, blank form: clear the canvas and reset all form state. The
    // Template/title are collected later, when Save draft / Publish is clicked.
    try {
      designer.setCurrentTree(transformToTreeNode(EMPTY_TREE));
    } catch (e) {
      // ignore — worst case the previous tree stays until the user edits it
    }
    setFormId(null);
    setMeta(EMPTY_META);
    setPendingAction(null);
  };

  // ------------------------------------------------------------------------ //
  return (
    <Space style={{ marginRight: 10 }}>
      <Text style={{ maxWidth: 220 }} ellipsis>
        {formId ? `Editing: ${meta.title}` : meta.title || "Untitled form"}
      </Text>
      <Button icon={<SettingOutlined />} onClick={() => setMetaOpen(true)}>
        Form settings
      </Button>
      <Button icon={<FolderOpenOutlined />} onClick={openPicker}>
        Open
      </Button>
      <Button onClick={startNew}>New</Button>
      <Button loading={saving} onClick={() => requestSave(false)}>
        Save draft
      </Button>
      <Button type="primary" loading={saving} onClick={() => requestSave(true)}>
        Publish
      </Button>
      <Button type="link" onClick={() => onSignOut && onSignOut()}>
        Sign out
      </Button>

      {/* Metadata modal */}
      <Modal
        title="Form settings"
        visible={metaOpen}
        onOk={handleMetaDone}
        onCancel={handleMetaCancel}
        okText={
          pendingAction === "publish"
            ? "Done & Publish"
            : pendingAction === "draft"
            ? "Done & Save"
            : "Done"
        }
        confirmLoading={saving}
      >
        <Form layout="vertical">
          <Form.Item label="Template type" required>
            <Select
              value={meta.templateTypeId}
              placeholder="Select template type"
              onChange={(v) =>
                setMeta({ ...meta, templateTypeId: v, templateId: undefined })
              }
            >
              {templateTypes.map((tt) => (
                <Option key={tt.id} value={tt.id}>
                  {tt.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Template" required>
            <Select
              value={meta.templateId}
              placeholder="Select template"
              disabled={!meta.templateTypeId}
              onChange={(v) => setMeta({ ...meta, templateId: v })}
            >
              {templatesForType(meta.templateTypeId).map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Form title" required>
            <Input
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              placeholder="e.g. Gas Test"
            />
          </Form.Item>
          <Form.Item label="Type">
            <Select
              value={meta.type}
              onChange={(v) => setMeta({ ...meta, type: v })}
            >
              {FORM_TYPES.map((ft) => (
                <Option key={ft} value={ft}>
                  {ft}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item label="Phase" style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={meta.phase}
                style={{ width: "100%" }}
                onChange={(v) => setMeta({ ...meta, phase: v })}
              >
                {PHASES.map((p) => (
                  <Option key={p} value={p}>
                    {p}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Order" style={{ width: 120, marginBottom: 0 }}>
              <InputNumber
                min={1}
                value={meta.order}
                onChange={(v) => setMeta({ ...meta, order: v || 1 })}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          <Form.Item label="Version note (optional)">
            <Input
              value={meta.note}
              onChange={(e) => setMeta({ ...meta, note: e.target.value })}
              placeholder="What changed in this version"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Open existing modal */}
      <Modal
        title="Open an existing form"
        visible={openOpen}
        footer={null}
        onCancel={() => setOpenOpen(false)}
      >
        <Text>Template</Text>
        <Select
          style={{ width: "100%" }}
          placeholder="Select template"
          value={pickTemplateId}
          onChange={loadFormsForTemplate}
          showSearch
          optionFilterProp="children"
        >
          {templateTypes.map((tt) => (
            <Select.OptGroup key={tt.id} label={tt.name}>
              {(tt.templates || []).map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
        <Divider />
        {formsList.length === 0 ? (
          <Text type="secondary">Pick a template to list its forms.</Text>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {formsList.map((f) => (
              <Button
                key={f.id}
                block
                style={{ textAlign: "left" }}
                onClick={() => loadFormIntoDesigner(f)}
              >
                [{f.order}] {f.title} · {f.type}
                {f.current_version ? "" : " · (unpublished)"}
              </Button>
            ))}
          </Space>
        )}
      </Modal>
    </Space>
  );
});
